// imports
import Component from "minsky-kit/js/components/Component";
import LibManager from "minsky-kit/js/managers/LibManager";
import { Loader } from "@googlemaps/js-api-loader";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import styles from "../utils/mapStyles";

// private statics
const VERSION = "1.0.0";
const INIT_MARKER_SIZE = 40;
const ZOOMED_MARKER_SIZE = 75;
const MAX_CLUSTER_ZOOM = 11;

// class definition
export default class AnalystMap extends Component {
  // constructor
  constructor(args = {}, objectName = "AnalystMap") {
    args.debug = args.debug || true;

    // super
    super(args, objectName);

    this.analysts = args.analysts || [];

    //vars
    this.map = null;
    this.cluster = null;
    this.spiderfier = null;
    this.infoWindow = null;
    this.markers = [];

    //load maps
    if (typeof google === "object" && typeof google.maps === "object") {
      this.initMap();
    } else {
      const loader = new Loader({
        apiKey: "AIzaSyD1CofGSb2ZJWU2DFyUXKP5tRDp_oF3KKo",
        version: "weekly",
      });

      loader.load().then(() => {
        this.initMap();
      });
    }
  }

  destroy() {
    // super destroy
    super.destroy();
  }

  initMap() {
    const startCenter = { lat: 50.85552822844845, lng: 4.3768741437356855 };
    //setup basic map
    this.map = new google.maps.Map(this.$el, {
      zoom: 10,
      center: startCenter,
      styles: styles,
      zoomControl: true,
      mapTypeControl: false,
      maxZoom: 15,
    });

    this.infoWindow = new google.maps.InfoWindow();

    // Only load if not already available
    if (typeof OverlappingMarkerSpiderfier === "undefined") {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/OverlappingMarkerSpiderfier/1.0.3/oms.min.js";
      script.onload = () => {
        this.initSpiderfier();
      };
      document.head.appendChild(script);
    } else {
      this.initSpiderfier();
    }
  }

  initSpiderfier() {
    this.spiderfier = new OverlappingMarkerSpiderfier(this.map, {
      markersWontMove: true,
      markersWontHide: true,
      basicFormatEvents: true,
      circleFootSeparation: ZOOMED_MARKER_SIZE, // Distance from center in circle mode (default: 23)
      spiralFootSeparation: ZOOMED_MARKER_SIZE, // Distance between spiral points (default: 26)
      keepSpiderfied: true, // Keep spiderfied until user clicks a marker or the map (default: false)
      ignoreMapClick: true,
    });

    this.analysts.forEach(($a) => {
      const lat = $a.dataset.lat;
      const lon = $a.dataset.lon;
      const image = $a.querySelector(".analyst-teaser__image img");
      const imageUrl = image ? image.getAttribute("src") : null;

      if (lat && lon) {
        // add marker
        const settings = {
          position: { lat: Number(lat), lng: Number(lon) },
          map: this.map,
        };

        const marker = new google.maps.Marker(settings);
        setMarkerIcon(marker, imageUrl, INIT_MARKER_SIZE);
        this.markers.push(marker);

        // add spidefier
        google.maps.event.addListener(marker, "spider_click", (e) => {
          // Enlarge the marker
          setMarkerIcon(marker, imageUrl, ZOOMED_MARKER_SIZE);

          marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);

          // set infowindow
          const $infoWindowEl = $a.querySelector(".analyst-infowindow");
          if ($infoWindowEl) {
            this.infoWindow.setContent(setInfoWindowContent($infoWindowEl, $a));
            this.infoWindow.open(this.map, marker);
          }
        });

        // Restore marker size when info window closes
        google.maps.event.addListener(this.infoWindow, "closeclick", () => {
          const currentZoom = this.map.getZoom();
          if (currentZoom <= MAX_CLUSTER_ZOOM) {
            setMarkerIcon(marker, imageUrl, INIT_MARKER_SIZE);
          }
          marker.setZIndex(1);
        });

        this.spiderfier.addMarker(marker);
      }
    });

    // Listen for zoom changes
    this.previousZoom = this.map.getZoom();
    google.maps.event.addListener(this.map, "zoom_changed", () => {
      const currentZoom = this.map.getZoom();
      const wasBelow = this.previousZoom < MAX_CLUSTER_ZOOM;
      const isNowAbove = currentZoom >= MAX_CLUSTER_ZOOM;

      // Only update if crossing the threshold
      if (wasBelow !== !isNowAbove) {
        this.updateMarkerSizes();
      }

      this.previousZoom = currentZoom;
    });

    // init cluster
    const algorithm = new SuperClusterAlgorithm({ maxZoom: MAX_CLUSTER_ZOOM });
    this.cluster = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      algorithm: algorithm,
      renderer: {
        render: renderClusterIcon,
      },
    });

    fitBoundsToVisibleMarkers.call(this);
  }

  updateMarkerSizes() {
    const zoom = this.map.getZoom();
    const size =
      zoom >= MAX_CLUSTER_ZOOM ? ZOOMED_MARKER_SIZE : INIT_MARKER_SIZE;
    this.markers.forEach((marker) => {
      setMarkerIcon(marker, marker.getIcon().url, size);
    });
  }

  show() {
    super.show();
    this.circlePackery.layout();
  }

  hide() {
    super.hide();
  }

  // getters & setters
  static get version() {
    return VERSION;
  }
}

function fitBoundsToVisibleMarkers() {
  if (this.markers.length > 1) {
    const bounds = new google.maps.LatLngBounds();

    for (var i = 0; i < this.markers.length; i++) {
      if (this.markers[i].getVisible()) {
        bounds.extend(this.markers[i].getPosition());
      }
    }

    this.map.fitBounds(bounds);
  } else if (this.markers.length > 0) {
    this.map.setCenter(this.markers[0].getPosition());
  }
}

function setMarkerIcon(marker, url, size) {
  if (url !== undefined && url !== null) {
    marker.setIcon({
      url: url,
      scaledSize: new google.maps.Size(size, size),
    });
  }
}

function renderClusterIcon({ count, position }, stats) {
  // change color if this cluster has more markers than the mean cluster
  const color =
    count > Math.max(10, stats.clusters.markers.mean) ? "#ee7154" : "#33a5cb";

  // create svg url with fill color
  const svg = window.btoa(`
<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
  <circle cx="120" cy="120" opacity=".6" r="70" />
  <circle cx="120" cy="120" opacity=".3" r="90" />
  <circle cx="120" cy="120" opacity=".2" r="110" />
  <circle cx="120" cy="120" opacity=".1" r="130" />
</svg>`);

  // create marker using svg icon
  return new google.maps.Marker({
    position,
    icon: {
      url: `data:image/svg+xml;base64,${svg}`,
      scaledSize: new google.maps.Size(45, 45),
    },
    label: {
      text: String(count),
      color: "rgba(255,255,255,0.9)",
      fontSize: "12px",
    },
    // adjust zIndex to be above other markers
    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
  });
}

function setInfoWindowContent($infoWindowEl, $a) {
  const $infoWindow = $infoWindowEl.cloneNode(true);
  $infoWindow.classList.remove("noRender");

  const $button = document.createElement("a");
  $button.innerHTML = $a.querySelector(".analyst-teaser__button").innerHTML;
  $button.setAttribute("href", $a.getAttribute("href"));
  $button.classList.add("button");
  $infoWindow.appendChild($button);

  return $infoWindow;
}
